import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

/**
 * Forgot Password page.
 *
 * POSTs to /api/auth/forgot-password (always 200 — no enumeration leak).
 * Shows the same success message whether or not the email exists, so an
 * attacker can't probe for valid accounts.
 *
 * Server-side: if the email is registered, a single-use reset link
 * (sha256-hashed token, 30-min TTL) is emailed via Resend.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F7F5F2' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: '#1B2A4A' }}>Forgot password?</h1>
          <p className="mt-1 text-sm" style={{ color: '#6B7280' }}>We'll email a reset link if your account exists.</p>
        </div>

        {done ? (
          <div className="bg-white rounded-lg shadow-sm border p-6 text-center" style={{ borderColor: '#E5E1DC' }} data-testid="forgot-password-success">
            <p className="text-sm" style={{ color: '#1B2A4A' }}>
              If <strong>{email}</strong> is registered with ScaleBoard, a password-reset email is on its way.
            </p>
            <p className="text-[12px] mt-3" style={{ color: '#6B7280' }}>
              The link expires in 30 minutes. Check your spam/junk folder if it doesn't arrive in a few minutes.
            </p>
            <Link to="/login" className="block mt-5 text-[12px] underline" style={{ color: '#2A7B88' }}>
              Back to login
            </Link>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="forgot-password-form"
            className="bg-white rounded-lg shadow-sm border p-6 space-y-5"
            style={{ borderColor: '#E5E1DC' }}
          >
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1B2A4A' }}>Email</label>
              <input
                data-testid="forgot-password-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                placeholder="you@company.com"
                autoFocus
              />
            </div>

            {err && (
              <div className="text-sm py-2 px-3 rounded-md" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>
                {err}
              </div>
            )}

            <button
              data-testid="forgot-password-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 rounded-md text-sm font-medium text-white transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#1B2A4A' }}
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>

            <div className="text-center">
              <Link to="/login" data-testid="forgot-password-back-link" className="text-[12px] underline" style={{ color: '#6B7280' }}>
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
